import React from "react";

import { Box, Paginator, EmptyState, CircularProgress } from "@tiny/components";
import Search from "@tiny/icons/generated/Search";
import { SuppressionDetailsListProps } from "./SuppressionDetailsList.types";
import { useSuppressionDetailsList } from "./SuppressionDetailsList.hooks";
import { itemsPerPageOptions } from "./SuppressionDetailsList.helpers";
import SuppressionDetailsListHeader from "./SuppressionDetailsListHeader";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import SuppressionTable from "../SuppressionDetailsTable/SuppressionTable";
import DeleteSuppressionDetailsDialog from "./DeleteSuppressionDetailsDialog/DeleteSuppressionDetailsDialog";

function SuppressionDetailsList(props: SuppressionDetailsListProps) {
  const {
    showChip,
    suppressionId,
    enableEdit,
    onAfterDelete,
    hideSort,
    hideDelete,
    ...other
  } = props;

  const { mdUp } = useBreakpoints();

  const {
    activeCustomer,
    allSuppressionDetailsSelected,
    closeDelete,
    currentPage,
    deleteOpen,
    deleteSelectedSuppressionDetails,
    deselectAllSuppressionDetails,
    exportSuppressionDetails,
    handlePageChange,
    handleSizeChange,
    isLoading,
    isSearching,
    pageSize,
    searchSuppressionDetails,
    selectSubscriber,
    selected,
    selectedSuppressionDetailsIds,
    sort,
    sortSuppressionDetails,
    suppressions,
    successDelete,
    successEdit,
    toggleAllSuppressionDetails,
    totalElements,
    openDelete,
    columnsSettingsVisible,
    openColumnsSettings,
    closeColumnsSettings,
    openFilter,
    filterStatuses,
    filterTags,
    deselectTag,
    deselectStatus,
  } = useSuppressionDetailsList(props);

  const emptyStateTitle = "No Suppression list found";
  const loaderActions = () => {
    return;
  };

  const emptyStateMessage = isSearching ? (
    <span>
      Try some other search keywords.
      <br /> Tip: Try remembering the email address.
    </span>
  ) : mdUp ? (
    <span>Click the Import button to add new user to suppression list.</span>
  ) : null;

  return isLoading ? (
    <Box
      {...other}
      width="100%"
      height="600px"
      display="flex"
      flexDirection="column"
    >
      <Box>
        <SuppressionDetailsListHeader
          onDeleteClick={loaderActions}
          onDeselectAll={loaderActions}
          onExportClick={loaderActions}
          onSearchChange={loaderActions}
          onSortChange={loaderActions}
          onColumnsSettingsOpen={loaderActions}
          onFilterOpen={loaderActions}
          selected={[]}
          sort={[]}
          tags={[]}
          statuses={[]}
          onDeselectTag={loaderActions}
          onDeselectStatus={loaderActions}
          hideSort={true}
          hideDelete={true}
        />
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex="1 1 0"
      >
        <CircularProgress />
      </Box>
    </Box>
  ) : (
    <>
      <Box {...other}>
        <SuppressionDetailsListHeader
          onDeleteClick={deleteSelectedSuppressionDetails}
          onDeselectAll={deselectAllSuppressionDetails}
          onExportClick={exportSuppressionDetails as never}
          onSearchChange={searchSuppressionDetails}
          onSortChange={sortSuppressionDetails}
          onColumnsSettingsOpen={openColumnsSettings}
          onFilterOpen={openFilter}
          selected={selectedSuppressionDetailsIds}
          sort={sort}
          tags={filterTags}
          statuses={filterStatuses}
          onDeselectTag={deselectTag}
          onDeselectStatus={deselectStatus}
          hideSort={hideSort}
          hideDelete={true}
        />
        <SuppressionTable
          suppressions={suppressions}
          selected={selectedSuppressionDetailsIds}
          enableEdit={enableEdit}
          onToggleSelect={selectSubscriber}
          allSelected={allSuppressionDetailsSelected}
          onToggleSelectAll={toggleAllSuppressionDetails}
          onDelete={openDelete as never}
          onAfterEdit={successEdit as never}
          columnsSettingsVisible={columnsSettingsVisible}
          onColumnsSettingsClose={closeColumnsSettings}
          hideDelete={hideDelete}
        />
        {suppressions.length > 0 && (
          <Paginator
            mt={mdUp ? 5 : 3}
            ml={mdUp ? 6 : 4}
            mr={mdUp ? 6 : 4}
            pb={mdUp ? 5 : 3}
            itemsPerPage={pageSize}
            itemsPerPageLabel="Results"
            itemsPerPageOptions={itemsPerPageOptions}
            onItemsPerPageChange={handleSizeChange}
            onPageChange={handlePageChange}
            page={currentPage}
            totalCount={totalElements}
          />
        )}
        {!isLoading && suppressions.length === 0 && (
          <Box bgcolor="grey.50" pt={10} pb={10}>
            <EmptyState
              title={emptyStateTitle}
              message={emptyStateMessage}
              icon={<Search />}
              iconColor="primary"
              circleColor="white"
              align="center"
            />
          </Box>
        )}
      </Box>
      <DeleteSuppressionDetailsDialog
        open={deleteOpen}
        onClose={closeDelete}
        suppressions={activeCustomer ? [activeCustomer] : selected}
        onSuccess={successDelete}
        suppressionId={suppressionId}
      />
    </>
  );
}

export default SuppressionDetailsList;
